import { useEffect, useCallback } from 'react';
import { useEditorStore } from '@/stores/editorStore';
import type { Tool } from '@/types/editor';
import { TOOL_SHORTCUTS } from '@/types/editor';

type ComposerRef = React.MutableRefObject<unknown>;

interface ComposerLike {
  current: {
    undo?: () => void;
    redo?: () => void;
    removeElements?: (ids: string[]) => void;
    getSelection?: () => string[];
    clearSelection?: () => void;
    selectAll?: () => void;
  } | null;
}

export function useEditorKeyboard(
  setActiveTool: (tool: Tool) => void,
  composerRef: ComposerRef
) {
  const { selectedElementIds, clearSelection } = useEditorStore();

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Don't handle if typing in an input or textarea
    const target = e.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable
    ) {
      return;
    }

    const key = e.key.toLowerCase();
    const isModified = e.ctrlKey || e.metaKey;
    const isShift = e.shiftKey;
    const composer = composerRef as ComposerLike;

    // Tool shortcuts (only when no modifier key)
    if (!isModified && !isShift) {
      const tool = TOOL_SHORTCUTS[key];
      if (tool) {
        e.preventDefault();
        setActiveTool(tool);
        return;
      }
    }

    // Other shortcuts
    switch (key) {
      case 'escape': {
        e.preventDefault();
        clearSelection();
        if (composer.current?.clearSelection) {
          composer.current.clearSelection();
        }
        break;
      }

      case 'delete':
      case 'backspace': {
        if (selectedElementIds.length > 0) {
          e.preventDefault();
          if (composer.current?.removeElements) {
            composer.current.removeElements(selectedElementIds);
          }
        }
        break;
      }

      case 'z': {
        if (isModified) {
          e.preventDefault();
          if (isShift) {
            // Redo: Ctrl+Shift+Z
            if (composer.current?.redo) {
              composer.current.redo();
            }
          } else {
            // Undo: Ctrl+Z
            if (composer.current?.undo) {
              composer.current.undo();
            }
          }
        }
        break;
      }

      case 'y': {
        if (isModified) {
          // Alternative redo: Ctrl+Y
          e.preventDefault();
          if (composer.current?.redo) {
            composer.current.redo();
          }
        }
        break;
      }

      case 'a': {
        if (isModified) {
          // Select all: Ctrl+A
          e.preventDefault();
          if (composer.current?.selectAll) {
            composer.current.selectAll();
          }
        }
        break;
      }
    }
  }, [setActiveTool, selectedElementIds, clearSelection, composerRef]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}
